import { useEffect, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Checkbox, Chip, styled, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { ILabelValueModel } from 'src/models';

import RHFAutocomplete from './RHFAutocomplete';

interface RHFMultiCheckSelectProps {
  label: string;
  name: string;
  options: ILabelValueModel[]; // select options 목록
  disabled?: boolean;
  // 이미 선택되어 있는 리스트
  selectedList?: any[] | null;
  // label을 동적으로 결정할 때 사용 (예: label이 없고 다른 속성으로 레이블을 결정해야 할 때 사용)
  getOptionLabel?: (option: any) => string;
  readonly?: boolean;
}

/**
 * ## RHFMultiAutocomplete 기본 사용법
 *
 * > React Hook Form과 통합되어 쉽게 폼 데이터를 관리하는 컴포넌트
 * > 다중 선택: 여러 항목을 선택할 수 있으며, 선택된 항목은 태그 형태로 표시
 * > 체크박스: 각 항목에 체크박스가 있어 선택 상태 확인
 * > 자동 완성: 입력한 값에 따라 옵션을 필터링하여 자동 완성 기능
 */

const RHFMultiAutocomplete = observer(function RHFMultiAutocomplete({
  name,
  label,
  options,
  disabled = false,
  getOptionLabel,
  selectedList,
  readonly = false,
  ...other
}: RHFMultiCheckSelectProps) {
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const { control } = useFormContext();
  const {
    field: { value, onChange },
  } = useController({ name, control });

  //  선택한 리스트 state
  const [selectedItems, setSelectedItems] = useState<any[]>([]);

  // 폼의 기본 값 또는 수정 시 초기 값 설정
  useEffect(() => {
    setSelectedItems(value || []);
  }, [value]);

  // 초기에 이미 선택된 데이터가 있다면 적용
  useEffect(() => {
    if (selectedList && selectedList.length > 0) {
      const selectedItems = options.filter((row) =>
        selectedList.some(
          (selected: any) => getOptionLabel && getOptionLabel(selected) === row.label,
        ),
      );
      getOptionLabel ? setSelectedItems(selectedItems) : setSelectedItems(selectedList);
    }
  }, [selectedList, options, getOptionLabel]);

  // 등록/수정 시 레이블을 동적으로 결정
  const resolveOptionLabel = (option: any) => {
    return option.label || (getOptionLabel ? getOptionLabel(option) : '');
  };

  const handleToggle = (option: any) => {
    // 이미 존재하는 item인지 확인
    const exists = selectedItems.some((item) => item.value === option.value);
    // 이미 존재하는 item이면 삭제, 아니면 추가
    const updatedItems = exists
      ? selectedItems.filter((item) => item.value !== option.value)
      : [...selectedItems, option];
    // 선택된 항목 변경
    setSelectedItems(updatedItems);
    onChange(updatedItems);
  };

  // chip버튼 삭제 기능
  const handleDelete = (optionToDelete: ILabelValueModel) => () => {
    const filteredItems = selectedItems.filter((item) => item.value !== optionToDelete.value);
    setSelectedItems(filteredItems);
    onChange(filteredItems);
  };

  return (
    <Container>
      <RHFAutocomplete
        name={name}
        label={label}
        options={options} // selsect options 목록
        multiple // 다중 선택
        clearOnEscape // 한번에 모두 삭제
        disableCloseOnSelect
        value={selectedItems}
        size="small"
        isOptionEqualToValue={(option, value) => option.value === value.value}
        onChange={(_, newValue) => {
          setSelectedItems(newValue);
          onChange(newValue);
        }}
        renderTags={(tagValue, getTagProps) => {
          return tagValue.map((option, index) => (
            <Chip
              label={resolveOptionLabel(option)}
              size={'small'}
              {...getTagProps({ index })}
              key={index}
              onDelete={handleDelete(option)}
              disabled={readonly}
            />
          ));
        }}
        getOptionLabel={resolveOptionLabel}
        renderOption={(props, option) => (
          <li {...props} key={option.value} onClick={() => handleToggle(option)}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              sx={{ marginRight: 8 }}
              checked={selectedItems.some((item) => item.value === option.value)}
            />
            <StyledText>{option.label}</StyledText>
          </li>
        )}
        sx={{ flexGrow: 1 }}
        readOnly={readonly}
        {...other}
      />
    </Container>
  );
});

export default RHFMultiAutocomplete;

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
}));

const StyledText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 12,
}));
